"use client"

import * as React from "react"
import { toast } from "sonner"
import { UserProfile, Account, Workspace, InstalledApp, App } from "@/types/workspace"
import { Language } from "@/lib/translations"

export function useDashboardHandlers({
  user,
  setUser,
  accounts,
  setAccounts,
  currentAccount,
  setCurrentAccount,
  currentWorkspace,
  setCurrentWorkspace,
  installedApps,
  setInstalledApps,
  setMembers,
  setProfileRole,
  setAllCustomRoles,
  setSelectedGroupedProfileName,
  setIsCreatingNewProfile,
  setProfileEditingName,
  setProfileAppPerms,
  setCustomRolesList,
  setUserCustomRolesMap,
  setApiKeys,
  setDeveloperApps,
  setSamlConfig,
  setMarketplaceApps,
  setConfigInstalledApps,
  setConfigAppIdToEdit,
  setDesktopWallpaper,
  setMobileWallpaper,
  setUseSeparateMobileWallpaper,
  setLang,
  setTopBarConfig,
  setCustomWallpapers,
  setAlarms,
  setAppPositions,
  setDesktopWidgets,
  setDesktopWidgetPositions,
  setWidgetsConfig,
  loadWorkspacePreferences,
  setProfileName,
  setProfileAvatarUrl,
  setProfilePhone,
  setLoginMusicUrl,
  setIsLoginMusicEnabled,
  setFullSizeScreen,
  setLockscreenPin,
  setCompanyName,
  setCompanyLogoUrl,
  setCompanyBannerUrl,
  setCompanyDocument,
  setCompanyBio,
  setCompanyCategory,
  setCompanyLocation,
  setCompanyWebsite,
  setLoading,
  lang,
  router,
  playNotificationSound,
  t
}: {
  user: UserProfile | null
  setUser: React.Dispatch<React.SetStateAction<UserProfile | null>>
  accounts: Account[]
  setAccounts: React.Dispatch<React.SetStateAction<Account[]>>
  currentAccount: Account | null
  setCurrentAccount: React.Dispatch<React.SetStateAction<Account | null>>
  currentWorkspace: Workspace | null
  setCurrentWorkspace: React.Dispatch<React.SetStateAction<Workspace | null>>
  installedApps: InstalledApp[]
  setInstalledApps: React.Dispatch<React.SetStateAction<InstalledApp[]>>
  setMembers: React.Dispatch<React.SetStateAction<any[]>>
  setProfileRole: React.Dispatch<React.SetStateAction<string>>
  setAllCustomRoles: React.Dispatch<React.SetStateAction<any[]>>
  setSelectedGroupedProfileName: React.Dispatch<React.SetStateAction<string | null>>
  setIsCreatingNewProfile: React.Dispatch<React.SetStateAction<boolean>>
  setProfileEditingName: React.Dispatch<React.SetStateAction<string>>
  setProfileAppPerms: React.Dispatch<React.SetStateAction<Record<string, { access: boolean; c: boolean; r: boolean; u: boolean; d: boolean }>>>
  setCustomRolesList: React.Dispatch<React.SetStateAction<any[]>>
  setUserCustomRolesMap: React.Dispatch<React.SetStateAction<Record<string, string[]>>>
  setApiKeys: React.Dispatch<React.SetStateAction<any[]>>
  setDeveloperApps: React.Dispatch<React.SetStateAction<any[]>>
  setSamlConfig: React.Dispatch<React.SetStateAction<any>>
  setMarketplaceApps: React.Dispatch<React.SetStateAction<App[]>>
  setConfigInstalledApps: React.Dispatch<React.SetStateAction<InstalledApp[]>>
  setConfigAppIdToEdit: React.Dispatch<React.SetStateAction<string | null>>
  setDesktopWallpaper: (wallpaper: string) => void
  setMobileWallpaper: (wallpaper: string) => void
  setUseSeparateMobileWallpaper: (separate: boolean) => void
  setLang: (lang: Language) => void
  setTopBarConfig: React.Dispatch<React.SetStateAction<any>>
  setCustomWallpapers: React.Dispatch<React.SetStateAction<string[]>>
  setAlarms: React.Dispatch<React.SetStateAction<any[]>>
  setAppPositions: React.Dispatch<React.SetStateAction<any>>
  setDesktopWidgets: React.Dispatch<React.SetStateAction<any>>
  setDesktopWidgetPositions: React.Dispatch<React.SetStateAction<any>>
  setWidgetsConfig?: React.Dispatch<React.SetStateAction<any[]>>
  loadWorkspacePreferences?: (workspaceId: string, token: string) => Promise<void>
  setProfileName: (name: string) => void
  setProfileAvatarUrl: (url: string | null) => void
  setProfilePhone: (phone: string) => void
  setLoginMusicUrl?: (url: string) => void
  setIsLoginMusicEnabled?: (enabled: boolean) => void
  setFullSizeScreen?: (enabled: boolean) => void
  setLockscreenPin?: (pin: string) => void
  setCompanyName: (name: string) => void
  setCompanyLogoUrl: (url: string | null) => void
  setCompanyBannerUrl?: (url: string | null) => void
  setCompanyDocument?: (doc: string) => void
  setCompanyBio?: (bio: string) => void
  setCompanyCategory?: (cat: string) => void
  setCompanyLocation?: (loc: string) => void
  setCompanyWebsite?: (web: string) => void
  setLoading: (loading: boolean) => void
  lang: Language
  router: any
  playNotificationSound: () => void
  t: any
}) {
  const showToast = React.useCallback(
    (title: string, description?: string, variant: "default" | "destructive" | "success" = "default") => {
      toast.dismiss()
      const options = {
        description,
        action: {
          label: lang === "pt" ? "Fechar Todas" : lang === "es" ? "Cerrar Todas" : "Dismiss All",
          onClick: () => toast.dismiss()
        }
      }

      if (variant === "destructive") {
        toast.error(title, options)
      } else if (variant === "success") {
        toast.success(title, options)
      } else {
        toast(title, options)
      }
      playNotificationSound()
    },
    [playNotificationSound, lang]
  )

  const fetchMarketplaceApps = React.useCallback(async () => {
    const token = document.cookie
      .split("; ")
      .find((row) => row.startsWith("token="))
      ?.split("=")[1]
    if (!token) return

    try {
      const res = await fetch("/api/graphql", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          query: `
            query GetMarketplaceApps {
              marketplaceApps(status: "active") {
                id
                name
                slug
                description
                iconUrl
                iframeUrl
                type
              }
            }
          `
        })
      })
      const result = await res.json()
      if (result.data && result.data.marketplaceApps) {
        setMarketplaceApps(result.data.marketplaceApps)
      }
    } catch (err) {
      console.error("Erro ao carregar aplicativos do marketplace:", err)
    }
  }, [setMarketplaceApps])

  const loadInstalledApps = React.useCallback(async (token: string, workspaceId: string) => {
    try {
      const res = await fetch("/api/graphql", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          query: `
            query GetInstalledApps($workspaceId: ID!) {
              installedApps(workspaceId: $workspaceId) {
                id
                status
                configuration
                app {
                  id
                  name
                  slug
                  description
                  iconUrl
                  iframeUrl
                  type
                  clientId
                }
              }
            }
          `,
          variables: { workspaceId }
        })
      })

      const result = await res.json()
      if (result.data && result.data.installedApps) {
        setInstalledApps(result.data.installedApps)
      }
    } catch (err) {
      console.error("Erro ao carregar apps instalados:", err)
    }
  }, [setInstalledApps])

  const loadDashboardData = React.useCallback(async (token: string, targetAccountId?: string) => {
    try {
      const res = await fetch("/api/graphql", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          query: `
            query InitDashboard {
              me {
                id
                name
                email
                avatarUrl
                twoFactorEnabled
                phone
                phoneVerifiedAt
                termsAcceptedAt
                loginMusicUrl
                loginMusicEnabled
                fullSizeScreen
                lockscreenPin
              }
              myAccounts {
                id
                name
                slug
                logoUrl
                bannerUrl
                document
                bio
                category
                location
                website
                workspaces {
                  id
                  name
                  slug
                  status
                  sortOrder
                  installedApps {
                    id
                    status
                    app {
                      id
                      name
                      iconUrl
                      iframeUrl
                      scopes
                      clientId
                    }
                  }
                }
              }
              accountProfiles {
                id
                userId
                role
                status
                user {
                  id
                  name
                  email
                  avatarUrl
                }
              }
            }
          `
        })
      })

      const result = await res.json()

      if (result.errors && result.errors.some((e: any) => e.extensions?.code === "UNAUTHENTICATED")) {
        document.cookie = "token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC;"
        if (typeof localStorage !== "undefined") {
          localStorage.removeItem("token")
          localStorage.removeItem("bold_token")
        }
        router.push("/login")
        return
      }

      const userData = result.data.me
      const accountsList = result.data.myAccounts
      const memberList = result.data.accountProfiles || []

      setUser(userData)
      if (userData) {
        setProfileName(userData.name || "")
        setProfileAvatarUrl(userData.avatarUrl ?? null)
        setProfilePhone(userData.phone ?? "")
        if (userData.loginMusicUrl !== undefined && userData.loginMusicUrl !== null) {
          setLoginMusicUrl?.(userData.loginMusicUrl)
          if (typeof localStorage !== "undefined") {
            localStorage.setItem(`loginMusicUrl:${userData.id}`, userData.loginMusicUrl)
            localStorage.setItem("loginMusicUrl", userData.loginMusicUrl)
          }
        } else if (typeof localStorage !== "undefined") {
          const savedMusic = localStorage.getItem(`loginMusicUrl:${userData.id}`) || localStorage.getItem("loginMusicUrl")
          if (savedMusic) setLoginMusicUrl?.(savedMusic)
        }
        if (userData.loginMusicEnabled !== undefined && userData.loginMusicEnabled !== null) {
          setIsLoginMusicEnabled?.(userData.loginMusicEnabled)
          if (typeof localStorage !== "undefined") {
            localStorage.setItem(`loginMusicEnabled:${userData.id}`, String(userData.loginMusicEnabled))
            localStorage.setItem("loginMusicEnabled", String(userData.loginMusicEnabled))
          }
        } else if (typeof localStorage !== "undefined") {
          const savedEnabled = localStorage.getItem(`loginMusicEnabled:${userData.id}`) ?? localStorage.getItem("loginMusicEnabled")
          if (savedEnabled !== null) setIsLoginMusicEnabled?.(savedEnabled === "true")
        }
        if (userData.fullSizeScreen !== undefined && userData.fullSizeScreen !== null) {
          setFullSizeScreen?.(userData.fullSizeScreen)
          if (typeof localStorage !== "undefined") {
            localStorage.setItem(`fullSizeScreen:${userData.id}`, String(userData.fullSizeScreen))
            localStorage.setItem("bold_fullscreen_dismissed_permanent", String(userData.fullSizeScreen))
          }
        } else if (typeof localStorage !== "undefined") {
          const savedFss = localStorage.getItem(`fullSizeScreen:${userData.id}`) ?? localStorage.getItem("bold_fullscreen_dismissed_permanent")
          if (savedFss !== null) setFullSizeScreen?.(savedFss === "true")
        }
        if (userData.lockscreenPin !== undefined && userData.lockscreenPin !== null) {
          setLockscreenPin?.(userData.lockscreenPin)
          if (typeof localStorage !== "undefined") {
            localStorage.setItem(`lockscreenPin:${userData.id}`, userData.lockscreenPin)
            localStorage.setItem("lockscreenPin", userData.lockscreenPin)
          }
        } else if (typeof localStorage !== "undefined") {
          const savedPin = localStorage.getItem(`lockscreenPin:${userData.id}`) || localStorage.getItem("lockscreenPin")
          if (savedPin) setLockscreenPin?.(savedPin)
        }
      }

      setAccounts(accountsList)
      setMembers(memberList)

      const myProf = memberList.find((p: any) => p.userId === userData.id)
      if (myProf) {
        setProfileRole(myProf.role)
      }

      const savedLang = localStorage.getItem("language") as Language
      if (savedLang) setLang(savedLang)

      const savedTopBar = localStorage.getItem(`topBarConfig:${userData.id}`)
      if (savedTopBar) {
        try {
          const parsed = JSON.parse(savedTopBar)
          setTopBarConfig((prev: any) => ({ ...prev, ...parsed }))
        } catch (e) {
          console.error("Erro ao carregar topBarConfig", e)
        }
      }

      const savedCustom = localStorage.getItem(`customWallpapers:${userData.id}`)
      if (savedCustom) {
        try {
          setCustomWallpapers(JSON.parse(savedCustom))
        } catch (e) {
          console.error("Erro ao carregar customWallpapers", e)
        }
      }

      const savedAlarms = localStorage.getItem(`alarms:${userData.id}`)
      if (savedAlarms) {
        setAlarms(JSON.parse(savedAlarms))
      }

      if (accountsList.length > 0) {
        let activeAcc: Account | null = null

        const pathParts = typeof window !== "undefined"
          ? window.location.pathname.split("/").filter(Boolean).map((p) => decodeURIComponent(p).toLowerCase())
          : []

        // 1. Tentar resolver a Conta a partir do targetAccountId ou do primeiro segmento da URL
        if (targetAccountId) {
          activeAcc = accountsList.find((a: Account) => a.id === targetAccountId) || null
        } else if (pathParts.length > 0) {
          const requestedSlug = pathParts[0]
          activeAcc = accountsList.find((a: Account) => a.slug?.toLowerCase() === requestedSlug || a.id?.toLowerCase() === requestedSlug) || null

          if (!activeAcc) {
            // A empresa solicitada na URL não existe ou o usuário não possui acesso.
            // Redirecionar para a tela inicial ("/") imediatamente sem piscar workspace e mantendo a tela de carregamento padrão.
            setCurrentAccount(null)
            setCurrentWorkspace(null)
            if (typeof localStorage !== "undefined") {
              localStorage.removeItem("currentAccount")
            }
            router.replace("/")
            return
          }
        }

        if (!activeAcc) {
          const savedAccStr = localStorage.getItem("currentAccount")
          if (savedAccStr) {
            try {
              const savedAcc = JSON.parse(savedAccStr)
              activeAcc = accountsList.find((a: Account) => a.id === savedAcc.id) || null
            } catch (e) {}
          }
        }

        if (!activeAcc) {
          activeAcc = accountsList[0] || null
        }

        if (activeAcc) {
          setCurrentAccount(activeAcc)
          setCompanyName(activeAcc.name || "")
          setCompanyLogoUrl(activeAcc.logoUrl ?? null)
          if (setCompanyBannerUrl) setCompanyBannerUrl(activeAcc.bannerUrl ?? null)
          if (setCompanyDocument) setCompanyDocument(activeAcc.document ?? "")
          if (setCompanyBio) setCompanyBio(activeAcc.bio ?? "")
          if (setCompanyCategory) setCompanyCategory(activeAcc.category ?? "")
          if (setCompanyLocation) setCompanyLocation(activeAcc.location ?? "")
          if (setCompanyWebsite) setCompanyWebsite(activeAcc.website ?? "")
          localStorage.setItem("currentAccount", JSON.stringify(activeAcc))

          // 2. Resolver o Workspace a partir das partes da URL
          if (activeAcc.workspaces && activeAcc.workspaces.length > 0) {
            let activeWs: Workspace | null = null

            if (pathParts.length > 1) {
              const requestedWs = pathParts[1]
              activeWs = activeAcc.workspaces.find(
                (w) => (w.slug?.toLowerCase() === requestedWs || w.id?.toLowerCase() === requestedWs) && w.status !== false
              ) || null

              if (!activeWs) {
                // O workspace solicitado na URL não existe, está arquivado ou o usuário não tem acesso
                setCurrentAccount(null)
                setCurrentWorkspace(null)
                if (typeof localStorage !== "undefined") {
                  localStorage.removeItem("currentAccount")
                }
                router.replace("/")
                return
              }
            } else {
              activeWs = activeAcc.workspaces.find((w) => w.status !== false) || activeAcc.workspaces[0]
            }

            setCurrentWorkspace(activeWs)

            if (loadWorkspacePreferences) {
              await loadWorkspacePreferences(activeWs.id, token)
            }

            await loadInstalledApps(token, activeWs.id)
          } else {
            // Conta sem nenhum workspace com acesso para este usuário
            setCurrentAccount(null)
            setCurrentWorkspace(null)
            setInstalledApps([])
            if (typeof localStorage !== "undefined") {
              localStorage.removeItem("currentAccount")
            }
            router.replace("/")
            return
          }
        }
      } else {
        setCurrentAccount(null)
        setCurrentWorkspace(null)
        setInstalledApps([])
        if (typeof localStorage !== "undefined") {
          localStorage.removeItem("currentAccount")
        }
        router.replace("/")
        return
      }

      // Buscar aplicativos do marketplace no carregamento inicial
      await fetchMarketplaceApps()
    } catch (err) {
      console.error("Erro ao carregar dados do painel:", err)
    } finally {
      setLoading(false)
    }
  }, [router, setUser, setProfileName, setProfileAvatarUrl, setProfilePhone, setLoginMusicUrl, setIsLoginMusicEnabled, setFullSizeScreen, setLockscreenPin, setDesktopWallpaper, setMobileWallpaper, setUseSeparateMobileWallpaper, setAccounts, setMembers, setProfileRole, setLang, setTopBarConfig, setCustomWallpapers, setAlarms, setCurrentAccount, setCompanyName, setCompanyLogoUrl, setCurrentWorkspace, setAppPositions, setDesktopWidgets, setDesktopWidgetPositions, loadWorkspacePreferences, loadInstalledApps, setInstalledApps, fetchMarketplaceApps, setLoading])

  const fetchMembers = React.useCallback(async () => {
    const token = document.cookie
      .split("; ")
      .find((row) => row.startsWith("token="))
      ?.split("=")[1]
    if (!token || !currentAccount) return
    try {
      const res = await fetch("/api/graphql", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          query: `
            query GetMembers {
              accountProfiles {
                id
                userId
                role
                status
                user {
                  id
                  name
                  email
                  avatarUrl
                }
              }
            }
          `
        })
      })
      const result = await res.json()
      if (result.data && result.data.accountProfiles) {
        setMembers(result.data.accountProfiles)
        const myProf = result.data.accountProfiles.find((p: any) => p.userId === user?.id)
        if (myProf) {
          setProfileRole(myProf.role)
        }
      }
    } catch (e) {
      console.error(e)
    }
  }, [currentAccount, user, setMembers, setProfileRole])

  const fetchAllCustomRoles = React.useCallback(async () => {
    const token = document.cookie
      .split("; ")
      .find((row) => row.startsWith("token="))
      ?.split("=")[1]
    if (!token) return
    try {
      const res = await fetch("/api/graphql", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          query: `
            query GetAccountCustomRoles {
              accountCustomRoles {
                id
                accountId
                appId
                name
                scopes
                createdAt
                updatedAt
              }
            }
          `
        })
      })
      const result = await res.json()
      if (result.data && result.data.accountCustomRoles) {
        setAllCustomRoles(result.data.accountCustomRoles)
      }
    } catch (e) {
      console.error("Erro ao buscar perfis:", e)
    }
  }, [setAllCustomRoles])

  const fetchCustomRoles = React.useCallback(async (appId: string) => {
    const token = document.cookie
      .split("; ")
      .find((row) => row.startsWith("token="))
      ?.split("=")[1]
    if (!token) return
    try {
      const res = await fetch("/api/graphql", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          query: `
            query GetCustomRoles($appId: ID!) {
              customRoles(appId: $appId) {
                id
                accountId
                appId
                name
                scopes
              }
            }
          `,
          variables: { appId }
        })
      })
      const result = await res.json()
      if (result.data && result.data.customRoles) {
        setCustomRolesList(result.data.customRoles)
      }
    } catch (e) {
      console.error(e)
    }
  }, [setCustomRolesList])

  const fetchUserCustomRoles = React.useCallback(async (userId: string) => {
    const token = document.cookie
      .split("; ")
      .find((row) => row.startsWith("token="))
      ?.split("=")[1]
    if (!token) return
    try {
      const res = await fetch("/api/graphql", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          query: `
            query GetUserCustomRoles($userId: ID!) {
              userCustomRoles(userId: $userId) {
                id
                name
                appId
              }
            }
          `,
          variables: { userId }
        })
      })
      const result = await res.json()
      if (result.data && result.data.userCustomRoles) {
        setUserCustomRolesMap(prev => ({
          ...prev,
          [userId]: result.data.userCustomRoles.map((r: any) => r.id)
        }))
      }
    } catch (e) {
      console.error(e)
    }
  }, [setUserCustomRolesMap])

  const fetchUserWorkspaces = React.useCallback(async (userId: string) => {
    const token = document.cookie
      .split("; ")
      .find((row) => row.startsWith("token="))
      ?.split("=")[1]
    if (!token) return []
    try {
      const res = await fetch("/api/graphql", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          query: `
            query GetUserWorkspaceMemberships($userId: ID!) {
              userWorkspaceMemberships(userId: $userId) {
                id
                workspaceId
                role
              }
            }
          `,
          variables: { userId }
        })
      })
      const result = await res.json()
      if (result.data && result.data.userWorkspaceMemberships) {
        return result.data.userWorkspaceMemberships.map((m: any) => m.workspaceId)
      }
    } catch (e) {
      console.error(e)
    }
    return []
  }, [])

  const handleCreateWorkspace = React.useCallback(async (name: string, accountId: string) => {
    const token = document.cookie
      .split("; ")
      .find((row) => row.startsWith("token="))
      ?.split("=")[1]
    if (!token) throw new Error("Não autenticado")

    const res = await fetch("/api/graphql", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({
        query: `
          mutation CreateWorkspace($name: String!, $accountId: ID!) {
            createWorkspace(name: $name, accountId: $accountId) {
              id
              name
              slug
              status
              accountId
              sortOrder
            }
          }
        `,
        variables: { name, accountId }
      })
    })

    const result = await res.json()
    if (result.errors && result.errors.length > 0) {
      throw new Error(result.errors[0].message)
    }
    return result.data.createWorkspace
  }, [])

  const handleUpdateWorkspace = React.useCallback(async (id: string, name: string) => {
    const token = document.cookie
      .split("; ")
      .find((row) => row.startsWith("token="))
      ?.split("=")[1]
    if (!token) throw new Error("Não autenticado")

    const res = await fetch("/api/graphql", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({
        query: `
          mutation UpdateWorkspace($id: ID!, $name: String!) {
            updateWorkspace(id: $id, name: $name) {
              id
              name
              slug
              status
            }
          }
        `,
        variables: { id, name }
      })
    })

    const result = await res.json()
    if (result.errors && result.errors.length > 0) {
      throw new Error(result.errors[0].message)
    }
    return result.data.updateWorkspace
  }, [])

  const handleArchiveWorkspace = React.useCallback(async (id: string) => {
    const token = document.cookie
      .split("; ")
      .find((row) => row.startsWith("token="))
      ?.split("=")[1]
    if (!token) throw new Error("Não autenticado")

    const res = await fetch("/api/graphql", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({
        query: `
          mutation ArchiveWorkspace($id: ID!) {
            archiveWorkspace(id: $id) {
              id
              name
              slug
              status
              sortOrder
            }
          }
        `,
        variables: { id }
      })
    })

    const result = await res.json()
    if (result.errors && result.errors.length > 0) {
      throw new Error(result.errors[0].message)
    }

    const archivedWs = result.data.archiveWorkspace

    if (currentAccount) {
      currentAccount.workspaces = (currentAccount.workspaces || []).map((w) =>
        w.id === id ? { ...w, status: false } : w
      )

      if (currentWorkspace?.id === id) {
        const remainingActive = (currentAccount.workspaces || []).filter((w) => w.id !== id && w.status !== false)
        setLoading(true)
        const targetWorkspace = remainingActive[0]
        if (currentAccount.slug && targetWorkspace?.slug) {
          window.location.href = `/${currentAccount.slug}/${targetWorkspace.slug}`
        } else if (currentAccount.slug) {
          window.location.href = `/${currentAccount.slug}`
        } else {
          window.location.href = "/"
        }
      }
    }

    return archivedWs
  }, [currentAccount, currentWorkspace, setLoading])

  const handleReactivateWorkspace = React.useCallback(async (id: string) => {
    const token = document.cookie
      .split("; ")
      .find((row) => row.startsWith("token="))
      ?.split("=")[1]
    if (!token) throw new Error("Não autenticado")

    const res = await fetch("/api/graphql", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({
        query: `
          mutation ReactivateWorkspace($id: ID!) {
            reactivateWorkspace(id: $id) {
              id
              name
              slug
              status
              sortOrder
            }
          }
        `,
        variables: { id }
      })
    })

    const result = await res.json()
    if (result.errors && result.errors.length > 0) {
      throw new Error(result.errors[0].message)
    }

    const reactivatedWs = result.data.reactivateWorkspace

    if (currentAccount) {
      currentAccount.workspaces = (currentAccount.workspaces || []).map((w) =>
        w.id === id ? { ...w, status: true } : w
      )
    }

    return reactivatedWs
  }, [currentAccount])

  const handleDeleteWorkspace = handleArchiveWorkspace

  const handleInviteUser = React.useCallback(async (
    email: string,
    name: string,
    role: string,
    workspaces?: Array<{ workspaceId: string; role: string }>
  ) => {
    const token = document.cookie
      .split("; ")
      .find((row) => row.startsWith("token="))
      ?.split("=")[1]
    if (!token) throw new Error("Não autenticado")

    const res = await fetch("/api/graphql", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({
        query: `
          mutation InviteUser($email: String!, $name: String!, $role: String!, $workspaces: [WorkspaceAccessInput!]) {
            inviteUser(email: $email, name: $name, role: $role, workspaces: $workspaces) {
              id
              userId
              role
              status
              user {
                id
                name
                email
                avatarUrl
              }
            }
          }
        `,
        variables: {
          email,
          name,
          role,
          workspaces: workspaces && workspaces.length > 0 ? workspaces : undefined
        }
      })
    })

    const result = await res.json()
    if (result.errors && result.errors.length > 0) {
      throw new Error(result.errors[0].message || "Erro ao convidar usuário")
    }

    await fetchMembers()
    return result.data?.inviteUser
  }, [fetchMembers])

  const handleUpdateAccountMember = React.useCallback(async (
    userId: string,
    data: { name?: string; role?: string; status?: string; customRoleId?: string | null }
  ) => {
    const token = document.cookie
      .split("; ")
      .find((row) => row.startsWith("token="))
      ?.split("=")[1]
    if (!token) throw new Error("Não autenticado")

    const res = await fetch("/api/graphql", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({
        query: `
          mutation UpdateAccountMember($userId: ID!, $name: String, $role: String, $status: String, $customRoleId: ID) {
            updateAccountMember(userId: $userId, name: $name, role: $role, status: $status, customRoleId: $customRoleId) {
              id
              userId
              role
              status
              user {
                id
                name
                email
                avatarUrl
              }
            }
          }
        `,
        variables: {
          userId,
          name: data.name,
          role: data.role,
          status: data.status,
          customRoleId: data.customRoleId
        }
      })
    })

    const result = await res.json()
    if (result.errors && result.errors.length > 0) {
      throw new Error(result.errors[0].message || "Erro ao atualizar membro")
    }

    await fetchMembers()
    return result.data?.updateAccountMember
  }, [fetchMembers])

  const handleRemoveUser = React.useCallback(async (userId: string) => {
    const token = document.cookie
      .split("; ")
      .find((row) => row.startsWith("token="))
      ?.split("=")[1]
    if (!token) throw new Error("Não autenticado")

    const res = await fetch("/api/graphql", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({
        query: `
          mutation RemoveAccountMember($userId: ID!) {
            removeAccountMember(userId: $userId)
          }
        `,
        variables: { userId }
      })
    })

    const result = await res.json()
    if (result.errors && result.errors.length > 0) {
      throw new Error(result.errors[0].message || "Erro ao remover membro")
    }

    const isCurrentUser = user && (user.id === userId || (user as any).userId === userId)
    if (isCurrentUser) {
      const remainingAccounts = accounts.filter((acc: any) => acc.id !== currentAccount?.id)
      setAccounts(remainingAccounts)
      setCurrentAccount(null)
      setCurrentWorkspace(null)
      if (typeof localStorage !== "undefined") {
        localStorage.removeItem("currentAccount")
      }
      router.push("/")
      return result.data?.removeAccountMember
    }

    await fetchMembers()
    return result.data?.removeAccountMember
  }, [user, accounts, currentAccount, fetchMembers, loadDashboardData, router, setAccounts, setCurrentAccount, setCurrentWorkspace])

  const handleSaveCompany = React.useCallback(async (data?: {
    name?: string
    logoUrl?: string | null
    bannerUrl?: string | null
    document?: string | null
    bio?: string | null
    category?: string | null
    location?: string | null
    website?: string | null
  }) => {
    const token = document.cookie
      .split("; ")
      .find((row) => row.startsWith("token="))
      ?.split("=")[1]
    if (!token) throw new Error("Não autenticado")

    const targetName = data?.name !== undefined ? data.name : (currentAccount?.name || "")
    if (!targetName?.trim()) {
      showToast("Nome obrigatório", "O nome da empresa não pode ficar em branco.", "destructive")
      return
    }

    const targetLogoUrl = data?.logoUrl !== undefined ? data.logoUrl : (currentAccount?.logoUrl || "")
    const targetBannerUrl = data?.bannerUrl !== undefined ? data.bannerUrl : (currentAccount?.bannerUrl || "")
    const targetDocument = data?.document !== undefined ? data.document : (currentAccount?.document || "")
    const targetBio = data?.bio !== undefined ? data.bio : (currentAccount?.bio || "")
    const targetCategory = data?.category !== undefined ? data.category : (currentAccount?.category || "")
    const targetLocation = data?.location !== undefined ? data.location : (currentAccount?.location || "")
    const targetWebsite = data?.website !== undefined ? data.website : (currentAccount?.website || "")

    const res = await fetch("/api/graphql", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({
        query: `
          mutation UpdateAccount(
            $name: String!
            $logoUrl: String
            $bannerUrl: String
            $document: String
            $bio: String
            $category: String
            $location: String
            $website: String
          ) {
            updateAccount(
              name: $name
              logoUrl: $logoUrl
              bannerUrl: $bannerUrl
              document: $document
              bio: $bio
              category: $category
              location: $location
              website: $website
            ) {
              id
              name
              slug
              logoUrl
              bannerUrl
              document
              bio
              category
              location
              website
            }
          }
        `,
        variables: {
          name: targetName,
          logoUrl: targetLogoUrl || "",
          bannerUrl: targetBannerUrl || "",
          document: targetDocument || "",
          bio: targetBio || "",
          category: targetCategory || "",
          location: targetLocation || "",
          website: targetWebsite || ""
        }
      })
    })

    const result = await res.json()
    if (result.errors && result.errors.length > 0) {
      showToast("Erro ao atualizar empresa", result.errors[0].message, "destructive")
      throw new Error(result.errors[0].message || "Erro ao atualizar empresa")
    }

    const updatedAcc = result.data?.updateAccount
    if (updatedAcc) {
      setCurrentAccount((prev: any) => prev ? { ...prev, ...updatedAcc } : updatedAcc)
      setAccounts((prev: any[]) => prev.map(acc => acc.id === updatedAcc.id ? { ...acc, ...updatedAcc } : acc))
      setCompanyName(updatedAcc.name || "")
      setCompanyLogoUrl(updatedAcc.logoUrl || null)
      setCompanyBannerUrl?.(updatedAcc.bannerUrl || null)
      setCompanyDocument?.(updatedAcc.document || "")
      setCompanyBio?.(updatedAcc.bio || "")
      setCompanyCategory?.(updatedAcc.category || "")
      setCompanyLocation?.(updatedAcc.location || "")
      setCompanyWebsite?.(updatedAcc.website || "")
      showToast("Empresa atualizada!", "Os dados da empresa foram salvos com sucesso.", "success")
    }
    return updatedAcc
  }, [
    currentAccount,
    setCurrentAccount,
    setAccounts,
    setCompanyName,
    setCompanyLogoUrl,
    setCompanyBannerUrl,
    setCompanyDocument,
    setCompanyBio,
    setCompanyCategory,
    setCompanyLocation,
    setCompanyWebsite,
    showToast
  ])

  return {
    showToast,
    loadDashboardData,
    loadInstalledApps,
    fetchMarketplaceApps,
    fetchMembers,
    fetchAllCustomRoles,
    fetchCustomRoles,
    fetchUserCustomRoles,
    fetchUserWorkspaces,
    handleCreateWorkspace,
    handleUpdateWorkspace,
    handleArchiveWorkspace,
    handleReactivateWorkspace,
    handleDeleteWorkspace,
    handleInviteUser,
    handleUpdateAccountMember,
    handleRemoveUser,
    handleSaveCompany
  }
}
